/**
 * Структурные значения реестра (объекты и списки) — словами, без JSON
 * (владелец 20.10: «тут json прям — это неправильно. Мы делаем инструмент
 * для людей, далёких от разработки»).
 *
 * У каждого такого поля в auto-ads свой `type` (`daily_quota`, `genders`,
 * `placements`, …) и русские подписи вариантов в `options` дескриптора.
 * Старый auto-ads присылает те же поля с `type: 'json'` и без `options` —
 * тип узнаётся по ключу (`adsTypeOf`), подписи — запасные (`FALLBACK_OPTIONS`).
 * Хранимые значения не меняются: только как их назвать.
 */
/** Поля, которые старый реестр присылал как `json`, → их тип в новом реестре. */
export const LEGACY_JSON_TYPES = {
    'opt.dailyQuota': 'daily_quota',
    'adset.genders': 'genders',
    'adset.placements': 'placements',
    'campaign.specialAdCategories': 'special_ad_categories',
    'adset.relaxation': 'audience_relaxation',
    'campaign.advantageLevers': 'advantage_levers',
    'creative.features': 'creative_features',
    'account.regionalRegulation': 'regional_regulation',
    'adset.valueRules': 'value_rules',
};
/** Типы со своим человеческим показом (и виджетом — `structured-widgets.tsx`). */
export const STRUCTURED_TYPES = new Set(Object.values(LEGACY_JSON_TYPES));
/** Тип поля для показа: новый тип реестра; `json` старого реестра — по ключу поля. */
export function adsTypeOf(desc) {
    if (desc.type === 'json')
        return LEGACY_JSON_TYPES[desc.key] ?? 'json';
    return desc.type;
}
/**
 * Подписи на случай старого реестра (без `options`). Новый реестр присылает
 * свои — они главнее (`optionsFor`).
 */
export const FALLBACK_OPTIONS = {
    daily_quota: [
        { value: 'warn', label: 'Предупредить' },
        { value: 'block', label: 'Запретить' },
        { value: 'off', label: 'Не проверять' },
    ],
    genders: [
        { value: 0, label: 'Все' },
        { value: 1, label: 'Мужчины' },
        { value: 2, label: 'Женщины' },
    ],
    placements: [
        { value: 'facebook', label: 'Facebook' },
        { value: 'facebook.feed', label: 'Лента' },
        { value: 'facebook.story', label: 'Истории' },
        { value: 'facebook.facebook_reels', label: 'Reels' },
        { value: 'facebook.video_feeds', label: 'Видео' },
        { value: 'facebook.marketplace', label: 'Marketplace' },
        { value: 'facebook.search', label: 'Результаты поиска' },
        { value: 'facebook.instream_video', label: 'Реклама внутри видео' },
        { value: 'facebook.right_hand_column', label: 'Правая колонка' },
        { value: 'facebook.profile_feed', label: 'Лента профиля' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'instagram.stream', label: 'Лента' },
        { value: 'instagram.story', label: 'Истории' },
        { value: 'instagram.reels', label: 'Reels' },
        { value: 'instagram.explore', label: 'Интересное' },
        { value: 'instagram.explore_home', label: 'Главная «Интересного»' },
        { value: 'instagram.profile_feed', label: 'Лента профиля' },
        { value: 'instagram.ig_search', label: 'Результаты поиска' },
        { value: 'messenger', label: 'Messenger' },
        { value: 'messenger.messenger_home', label: 'Главная Messenger' },
        { value: 'messenger.story', label: 'Истории' },
        { value: 'audience_network', label: 'Audience Network' },
        { value: 'audience_network.classic', label: 'Приложения и сайты партнёров' },
        { value: 'audience_network.rewarded_video', label: 'Видео с вознаграждением' },
    ],
    special_ad_categories: [
        { value: 'NONE', label: 'Нет' },
        { value: 'CREDIT', label: 'Кредиты' },
        { value: 'EMPLOYMENT', label: 'Работа и вакансии' },
        { value: 'HOUSING', label: 'Жильё' },
        { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Финансовые продукты и услуги' },
        { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Социальные темы, выборы и политика' },
        { value: 'ONLINE_GAMBLING_AND_GAMING', label: 'Азартные игры онлайн' },
    ],
    audience_relaxation: [
        { value: 'lookalike', label: 'Похожие аудитории (LAL)' },
        { value: 'custom_audience', label: 'Свои аудитории' },
    ],
    creative_features: [
        { value: 'advantage_plus_creative', label: 'Advantage+ креатив целиком' },
        { value: 'image_touchups', label: 'Ретушь изображения' },
        { value: 'image_animation', label: 'Анимация изображения' },
        { value: 'image_background_gen', label: 'Генерация фона (ИИ)' },
        { value: 'video_auto_crop', label: 'Автообрезка видео' },
        { value: 'text_optimizations', label: 'Варианты текста от Meta' },
        { value: 'enhance_cta', label: 'Усиление кнопки' },
        { value: 'inline_comment', label: 'Показ комментария под объявлением' },
        { value: 'audio', label: 'Музыка' },
    ],
};
/** Варианты поля: из реестра, иначе запасные по типу. */
export function optionsFor(type, fromRegistry) {
    return fromRegistry != null && fromRegistry.length > 0
        ? fromRegistry
        : (FALLBACK_OPTIONS[type] ?? []);
}
const isRec = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const labelIn = (opts, v) => opts.find((o) => String(o.value) === String(v))?.label;
const YES_NO = (v) => (v ? 'да' : 'нет');
/** Вкл./выкл. Meta: 1/0, «1»/«0», true/false, OPT_IN/OPT_OUT, ENABLED/DISABLED. */
function onOff(v) {
    if (v === true || v === 1 || v === '1' || v === 'OPT_IN' || v === 'ENABLED')
        return true;
    if (v === false || v === 0 || v === '0' || v === 'OPT_OUT' || v === 'DISABLED')
        return false;
    return null;
}
/**
 * Любое значение — читаемым текстом, без скобок и кавычек: список — через
 * запятую, объект — «ключ: значение; …», пусто — «нет». Последний рубеж для
 * значений, которым не нашлось правила.
 */
export function humanText(value) {
    if (value === undefined || value === null)
        return '—';
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number')
        return String(value).replace('.', ',');
    if (typeof value === 'boolean')
        return YES_NO(value);
    if (Array.isArray(value)) {
        return value.length === 0 ? 'нет' : value.map((x) => humanText(x)).join(', ');
    }
    if (isRec(value)) {
        const entries = Object.entries(value);
        if (entries.length === 0)
            return 'нет';
        return entries.map(([k, v]) => `${k}: ${humanText(v)}`).join('; ');
    }
    return String(value);
}
// --- по типам ------------------------------------------------------------------
export const QUOTA_THEN = {
    warn: 'предупредить',
    block: 'запретить',
};
function dailyQuotaText(v) {
    if (!isRec(v))
        return undefined;
    if (v.mode === 'off')
        return 'не проверяется';
    const then = QUOTA_THEN[String(v.mode)];
    if (typeof v.limit !== 'number' || then == null)
        return undefined;
    return `не больше ${v.limit} в день, при превышении — ${then}`;
}
function gendersText(v, opts) {
    if (v === null || v === undefined)
        return 'Все';
    if (!Array.isArray(v))
        return labelIn(opts, v);
    if (v.length === 0 || v.some((g) => String(g) === '0'))
        return 'Все';
    return v.map((g) => labelIn(opts, g) ?? String(g)).join(', ');
}
export const AUTO_PLACEMENTS = 'Автоматические (Advantage+)';
function placementsText(v, opts) {
    if (v === null || v === undefined)
        return AUTO_PLACEMENTS;
    if (Array.isArray(v)) {
        if (v.length === 0)
            return 'нет';
        return v.map((p) => labelIn(opts, p) ?? String(p)).join(', ');
    }
    if (typeof v === 'string')
        return labelIn(opts, v);
    return undefined;
}
function specialCategoriesText(v, opts) {
    if (v === null || v === undefined)
        return 'Нет';
    if (!Array.isArray(v))
        return typeof v === 'string' ? labelIn(opts, v) : undefined;
    const named = v.filter((c) => String(c) !== 'NONE');
    return named.length === 0 ? 'Нет' : named.map((c) => labelIn(opts, c) ?? String(c)).join(', ');
}
export const RELAXATION_TEXT = 'Как у «Advantage+ аудитории»: включена — Meta может расширять похожие аудитории (LAL); ' +
    'выключена — не расширяет ни похожие, ни свои аудитории';
function relaxationText(v, opts) {
    if (v === null || v === undefined)
        return RELAXATION_TEXT;
    if (!isRec(v))
        return undefined;
    // Meta отбрасывает ключи с 0: нет ключа — не расширяется
    const keys = new Set([...opts.map((o) => String(o.value)), ...Object.keys(v)]);
    return [...keys]
        .map((k) => {
        const on = onOff(v[k]) === true;
        return `${labelIn(opts, k) ?? k}: ${on ? 'расширяются' : 'не расширяются'}`;
    })
        .join('; ');
}
export const ADVANTAGE_LEVERS_TEXT = 'Бюджет — как выбран (на кампании или на группах), плейсменты — автоматические, ' +
    'аудитория — по переключателю «Advantage+ аудитория»';
const LEVER_NAMES = {
    advantage_budget_state: 'Бюджет на кампании',
    advantage_placement_state: 'Автоматические плейсменты',
    advantage_audience_state: 'Advantage+ аудитория',
    advantage_state: 'Метка Advantage+',
};
function leverState(v) {
    const b = onOff(v);
    return b == null ? undefined : b ? 'включён' : 'выключен';
}
function leversText(v) {
    if (v === null || v === undefined)
        return ADVANTAGE_LEVERS_TEXT;
    if (typeof v === 'string')
        return leverState(v);
    if (!isRec(v))
        return undefined;
    return Object.entries(v)
        .map(([k, x]) => `${LEVER_NAMES[k] ?? k}: ${leverState(x) ?? humanText(x)}`)
        .join('; ');
}
export const CREATIVE_FEATURES_TEXT = 'Все улучшения Meta выключены';
function featuresText(v, opts) {
    if (v === 'all_opt_out' || v === null || v === undefined)
        return CREATIVE_FEATURES_TEXT;
    if (typeof v === 'string') {
        const b = onOff(v);
        return b == null ? undefined : b ? 'вкл.' : 'выкл.';
    }
    if (!isRec(v))
        return undefined;
    // карта Meta `{ключ: {enroll_status}}`: назвать включённые
    const on = Object.entries(v)
        .filter(([, x]) => onOff(isRec(x) ? x.enroll_status : x) === true)
        .map(([k]) => labelIn(opts, k) ?? k);
    return on.length === 0 ? CREATIVE_FEATURES_TEXT : `Включены: ${on.join(', ')}`;
}
const REGION_NAMES = {
    TAIWAN: 'Тайвань',
    SINGAPORE: 'Сингапур',
    THAILAND: 'Таиланд',
    MALAYSIA: 'Малайзия',
    BRAZIL: 'Бразилия',
    AUSTRALIA: 'Австралия',
    INDIA: 'Индия',
    INDONESIA: 'Индонезия',
    PHILIPPINES: 'Филиппины',
    VIETNAM: 'Вьетнам',
    TURKEY: 'Турция',
    KOREA: 'Корея',
    JAPAN: 'Япония',
};
const REG_KIND = {
    UNIVERSAL: 'вся реклама',
    REGULATION: 'вся реклама',
    FINSERV: 'финансовые услуги',
    SECURITIES: 'ценные бумаги',
};
/** `TAIWAN_UNIVERSAL` → «Тайвань (вся реклама)»; незнакомое — как есть. */
export function regulatedCategoryName(code) {
    const parts = code.trim().toUpperCase().split('_');
    const region = REGION_NAMES[parts[0] ?? ''];
    if (!region)
        return code;
    const rest = parts.slice(1).join('_');
    if (rest === '')
        return region;
    return `${region} (${REG_KIND[rest] ?? rest.toLowerCase().replace(/_/g, ' ')})`;
}
/** `taiwan_universal_payer` → «Тайвань — плательщик»; `universal_beneficiary` → «бенефициар». */
export function regulationRoleName(role) {
    const parts = role.trim().toLowerCase().split('_');
    const last = parts[parts.length - 1];
    const what = last === 'payer' ? 'плательщик' : last === 'beneficiary' ? 'бенефициар' : role;
    const region = REGION_NAMES[(parts[0] ?? '').toUpperCase()];
    return region ? `${region} — ${what}` : what;
}
function regulationText(v) {
    if (v === null || v === undefined)
        return 'не задано в профиле кабинета';
    // сверка: категории приходят списком, идентичности — картой «роль → id»
    if (Array.isArray(v)) {
        return v.length === 0 ? 'нет' : v.map((c) => regulatedCategoryName(String(c))).join(', ');
    }
    if (!isRec(v))
        return undefined;
    if (Array.isArray(v.categories)) {
        const cats = v.categories.map((c) => regulatedCategoryName(String(c)));
        if (cats.length === 0)
            return 'не задано в профиле кабинета';
        const roles = isRec(v.identities) ? Object.keys(v.identities) : [];
        return roles.length === 0
            ? cats.join(', ')
            : `${cats.join(', ')}; указаны: ${roles.map(regulationRoleName).join(', ')}`;
    }
    return Object.entries(v)
        .map(([role, id]) => {
        const raw = isRec(id) ? id.id : id;
        return `${regulationRoleName(role)}: ${humanText(raw)}`;
    })
        .join('; ');
}
function valueRulesText(v) {
    if (v === null || v === undefined)
        return 'нет';
    if (typeof v === 'boolean' || v === 'true' || v === 'false') {
        return v === true || v === 'true' ? 'применены' : 'не применены';
    }
    if (!Array.isArray(v))
        return undefined;
    const n = v.length;
    if (n === 0)
        return 'нет';
    const m10 = n % 10;
    const m100 = n % 100;
    const word = m10 === 1 && m100 !== 11
        ? 'правило'
        : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)
            ? 'правила'
            : 'правил';
    return `${n} ${word}`;
}
/**
 * Значение структурного поля словами (`type` — уже через `adsTypeOf`).
 * `undefined` — тип не структурный или значение незнакомой формы (тогда
 * вызывающий берёт `humanText`).
 */
export function structuredText(type, value, registryOptions) {
    const opts = optionsFor(type, registryOptions);
    switch (type) {
        case 'daily_quota':
            return dailyQuotaText(value);
        case 'genders':
            return gendersText(value, opts);
        case 'placements':
            return placementsText(value, opts);
        case 'special_ad_categories':
            return specialCategoriesText(value, opts);
        case 'audience_relaxation':
            return relaxationText(value, opts);
        case 'advantage_levers':
            return leversText(value);
        case 'creative_features':
            return featuresText(value, opts);
        case 'regional_regulation':
            return regulationText(value);
        case 'value_rules':
            return valueRulesText(value);
        default:
            return undefined;
    }
}
/** Структурное значение словами, а незнакомую форму — `humanText`. Никогда не JSON. */
export function structuredOrHuman(desc, value) {
    return structuredText(adsTypeOf(desc), value, desc.options) ?? humanText(value);
}
/** Строка — это JSON-объект или JSON-список (сервер прислал показ «как есть»). */
export function parseJsonish(s) {
    const t = s.trim();
    if (!/^[[{]/.test(t) && t !== 'null')
        return { ok: false };
    try {
        return { ok: true, value: JSON.parse(t) };
    }
    catch {
        return { ok: false };
    }
}
//# sourceMappingURL=structured-display.js.map